import React from "react";
import styles from "./app-select.module.css";
import * as SelectPrimitive from "@radix-ui/react-select";
import {cn} from "@/lib/utils";
import {ChevronDownIcon, ChevronUpIcon} from "lucide-react";
import {ChevronDownSvg} from "@/app/components/icons/ChevronDownSvg";

function Select({...props}: React.ComponentProps<typeof SelectPrimitive.Root>) {
    return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectValue({...props}: React.ComponentProps<typeof SelectPrimitive.Value>) {
    return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
                           className,
                           size = "default",
                           children,
                           ...props
                       }: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
    size?: "sm" | "default"
}) {
    return (
        <SelectPrimitive.Trigger
            data-slot="select-trigger"
            data-size={size}
            className={cn(
                'flex items-center justify-between py-4 px-6 rounded-[8px] border-2 text-noble-black-300 leading-none bg-gray border-stroke-gray',
                styles.Trigger,
                className
            )}
            {...props}
        >
            {children}
            <SelectPrimitive.Icon className={cn('ml-2', styles.Icon)} asChild>
                <ChevronDownSvg/>
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    )
}

function SelectContent({
                           className,
                           header,
                           viewportClassName,
                           children,
                           position = "popper",
                           align = "center",
                            sideOffset = 8,
                           ...props
                       }: React.ComponentProps<typeof SelectPrimitive.Content> & {
    header?: React.ReactNode
    viewportClassName?: string
}) {
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                data-slot="select-content"
                className={cn(
                    'border-2 border-black-transparent-40 bg-noble-black-600 text-noble-black-0 rounded-xl w-(--radix-select-trigger-width) flex flex-col overflow-hidden',
                    className,
                )}
                position={position}
                align={align}
                sideOffset={sideOffset}
                {...props}
            >
                {header}
                <SelectScrollUpButton/>
                <SelectPrimitive.Viewport
                    className={cn(
                        position === "popper" &&
                        "w-full scroll-my-1",
                        viewportClassName
                    )}
                >
                    {children}
                </SelectPrimitive.Viewport>
                <SelectScrollDownButton/>
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    )
}

function SelectItem({className, children, ...props}: React.ComponentProps<typeof SelectPrimitive.Item>) {
    return (
        <SelectPrimitive.Item
            data-slot="select-item"
            className={cn(
                'py-4 px-6 leading-none m-[3px]',
                styles.Item,
                className
            )}
            {...props}
        >
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    )
}

function SelectScrollUpButton({className, ...props}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
    return (
        <SelectPrimitive.ScrollUpButton
            data-slot="select-scroll-up-button"
            className={cn(
                "flex cursor-default items-center justify-center py-1",
                className
            )}
            {...props}
        >
            <ChevronUpIcon className="size-4"/>
        </SelectPrimitive.ScrollUpButton>
    )
}

function SelectScrollDownButton({className, ...props}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
    return (
        <SelectPrimitive.ScrollDownButton
            data-slot="select-scroll-down-button"
            className={cn(
                "flex cursor-default items-center justify-center py-1",
                className
            )}
            {...props}
        >
            <ChevronDownIcon className="size-4"/>
        </SelectPrimitive.ScrollDownButton>
    )
}

export {
    Select,
    SelectContent,
    SelectItem,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectTrigger,
    SelectValue,
}
